import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { ActivityIndicator, Alert, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';

import type { AssociationSettingsHandle } from './association-settings-handle';

import {
	useAccountingPermissions, useCreateLateFeeRule, useDeleteLateFeeRule,
	useLateFeeRules, useUpdateLateFeeRule,
} from '@/hooks/use-accounting';
import { usePristinePayload } from '@/hooks/use-pristine-payload';
import type { LateFeeRule } from '@/types/models';
import { formatMoneyCents } from '@/utils/format-money';

interface Props {
	/** Edit mode: the association whose late-fee rules are loaded. Omit for the
	 *  create (draft) flow — a single empty rule editor is shown and `save(id)`
	 *  creates the rule only if the user opts in. */
	associationId?: string;
	/** When true, the parent owns saving via the imperative handle. */
	hideSaveButton?: boolean;
}

type FeeMode = 'amount' | 'percent';

const parseDollarsToCents = (s: string): number | null => {
	const n = Number(s.replace(/[^0-9.-]/g, ''));
	if (!isFinite(n)) return null;
	return Math.round(n * 100);
};

/**
 * Late-fee rules editor for an association. The intended invariant is one
 * active rule at a time; the API deactivates prior rules when a new active
 * rule is created. We render the active rule's editor first, then any
 * inactive (history) rules read-only beneath it.
 */
export const AssociationLateFeesSettings = forwardRef<AssociationSettingsHandle, Props>(({ associationId, hideSaveButton }, ref) => {
	const { canModify } = useAccountingPermissions();
	const { data, isLoading } = useLateFeeRules(associationId);
	const draft = !associationId;

	if (!draft && (isLoading || !data)) {
		return <View style={{ padding: 16, alignItems: 'center' }}><ActivityIndicator /></View>;
	}

	const active = data?.items.find((r) => r.active);
	const inactive = data?.items.filter((r) => !r.active) ?? [];

	return (
		<View>
			<RuleEditor ref={ref} associationId={associationId} rule={active} canModify={canModify} hideSaveButton={hideSaveButton} />
			{inactive.length > 0 && (
				<View style={styles.section}>
					<Text style={styles.sectionTitle}>Past rules</Text>
					{inactive.map((r) => (
						<View key={r.id} style={styles.historyRow}>
							<Text style={styles.historyLabel}>{describeRule(r)}</Text>
							{canModify && (
								<Pressable hitSlop={6} onPress={() => Alert.alert('Past rule', 'Inactive rules are kept for audit. Delete permanently?', [
									{ text: 'Cancel', style: 'cancel' },
									{ text: 'Delete', style: 'destructive' },
								])}>
									<Text style={styles.muted}>archived</Text>
								</Pressable>
							)}
						</View>
					))}
				</View>
			)}
		</View>
	);
});

AssociationLateFeesSettings.displayName = 'AssociationLateFeesSettings';

const describeRule = (r: LateFeeRule): string => {
	const fee = r.amountCents != null
		? formatMoneyCents(r.amountCents)
		: `${((r.percentBps ?? 0) / 100).toFixed(2)}% of overdue balance`;
	const cap = r.capCents != null ? `, capped at ${formatMoneyCents(r.capCents)}` : '';
	const grace = r.graceDays === 0 ? 'on the due date' : `${r.graceDays} days after the due date`;
	const max = r.maxPerCycle > 1 ? `, up to ${r.maxPerCycle}× per cycle` : '';
	return `${fee} ${grace}${max}${cap}`;
};

const RuleEditor = forwardRef<AssociationSettingsHandle, {
	associationId?: string;
	rule: LateFeeRule | undefined;
	canModify: boolean;
	hideSaveButton?: boolean;
}>(({ associationId, rule, canModify, hideSaveButton }, ref) => {
	const initialMode: FeeMode = rule?.percentBps != null ? 'percent' : 'amount';
	const [mode, setMode] = useState<FeeMode>(initialMode);
	const [grace, setGrace] = useState(String(rule?.graceDays ?? 15));
	const [amount, setAmount] = useState(rule?.amountCents != null ? (rule.amountCents / 100).toFixed(2) : '25.00');
	const [percent, setPercent] = useState(rule?.percentBps != null ? (rule.percentBps / 100).toFixed(2) : '5.00');
	const [maxPerCycle, setMaxPerCycle] = useState(String(rule?.maxPerCycle ?? 1));
	const [cap, setCap] = useState(rule?.capCents != null ? (rule.capCents / 100).toFixed(2) : '');
	const [active, setActive] = useState(rule?.active ?? true);
	/**
	 * Did the user engage with this editor at all — INCLUDING an interaction that changes
	 * nothing, such as re-selecting the fee type that is already selected.
	 *
	 * Deliberately not the same question as "did the payload change", and both are needed.
	 * With no rule yet, accepting the defaults IS a decision and must create a real $25
	 * rule, so mere engagement is the signal. With a rule already present, engagement is
	 * worthless — the section is mounted on every visit — so only a payload change may
	 * write. Collapsing the two made an untouched section 403 for a user who may read late
	 * fees but not save them, which is the bug this pairing exists to avoid.
	 */
	const [touched, setTouched] = useState(false);
	// Once a draft rule is created via the parent FAB (no pre-existing rule), keep
	// its id so a later retry UPDATES it instead of POSTing a duplicate that
	// deactivates the first. The draft editor never re-reads the rules list, so
	// this local id is the only record that the create already happened.
	const [createdRuleId, setCreatedRuleId] = useState<string | null>(null);

	const create = useCreateLateFeeRule();
	const update = useUpdateLateFeeRule();
	const del = useDeleteLateFeeRule();
	const pending = create.isPending || update.isPending || del.isPending;

	// Throws a user-facing message on invalid input. Callers own how to surface
	// it: the inline Save button Alerts; the parent FAB collects it into a
	// single partial-save toast (so the user never gets a double notification).
	const validate = useCallback((): { graceDays: number; amountCents?: number; percentBps?: number; maxPerCycle: number; capCents?: number } => {
		const g = Number(grace);
		if (!isFinite(g) || g < 0 || g > 365) throw new Error('Grace days must be 0–365');
		const m = Number(maxPerCycle);
		if (!isFinite(m) || m < 1 || m > 99) throw new Error('Max per cycle must be 1–99');
		const capC = cap.trim() === '' ? undefined : parseDollarsToCents(cap) ?? undefined;
		if (cap.trim() !== '' && capC == null) throw new Error('Invalid cap');
		if (mode === 'amount') {
			const cents = parseDollarsToCents(amount);
			if (cents == null || cents < 0) throw new Error('Invalid amount');
			return { graceDays: g, amountCents: cents, maxPerCycle: m, capCents: capC };
		}
		const pct = Number(percent);
		if (!isFinite(pct) || pct <= 0 || pct > 100) throw new Error('Percent must be 0–100');
		return { graceDays: g, percentBps: Math.round(pct * 100), maxPerCycle: m, capCents: capC };
	}, [grace, maxPerCycle, cap, mode, amount, percent]);

	// Persist via mutateAsync so the parent FAB can await + sequence and catch
	// failures. Updates an existing rule (pre-existing OR one we created on a prior
	// attempt); otherwise creates one against `id`. The create→update switch keeps
	// retries idempotent — re-running never posts a duplicate rule.
	/**
	 * The request body, for the changed-or-not comparison.
	 *
	 * Baseline is whatever the editor loaded with, so an existing rule reads clean until a
	 * value genuinely differs — and an edit that normalises back ("5" → "5.00") stays clean.
	 * This governs the EXISTING-rule case only; see `touched` for the create case.
	 */
	const ruleBody = useCallback(() => ({ ...validate(), active }), [validate, active]);
	const { isDirty, markSaved } = usePristinePayload(ruleBody);

	const persist = useCallback(async (id: string) => {
		const v = validate();
		const targetId = rule?.id ?? createdRuleId;
		if (targetId) {
			await update.mutateAsync({
				id: targetId,
				body: {
					graceDays: v.graceDays,
					amountCents: v.amountCents ?? null,
					percentBps: v.percentBps ?? null,
					maxPerCycle: v.maxPerCycle,
					capCents: v.capCents ?? null,
					active,
				},
			});
		} else {
			const created = await create.mutateAsync({ associationId: id, ...v, active });
			if (created?.id) setCreatedRuleId(created.id);
		}
		markSaved();
	}, [validate, rule, createdRuleId, active, update, create, markSaved]);

	const hasRule = !!(rule?.id ?? createdRuleId);

	useImperativeHandle(ref, () => ({
		save: async (id: string) => {
			/**
			 * Two different questions, by design:
			 *
			 *   existing rule → write only if a VALUE changed. The edit page flushes every
			 *     mounted section, so engagement means nothing here; this is what stops a
			 *     rename writing late-fee rules as a side effect (and 403ing for a user who
			 *     may read them but not save them).
			 *   no rule yet   → create only if the user ENGAGED. Accepting the defaults is a
			 *     real decision, and produces a real $25 rule, so an interaction that changes
			 *     no value still counts.
			 */
			if (hasRule ? !isDirty : !touched) return;
			await persist(id);
		},
	}), [hasRule, isDirty, touched, persist]);

	const save = () => {
		if (!associationId) return;
		void persist(associationId).catch((e) => Alert.alert('Save failed', e instanceof Error ? e.message : 'Unknown error'));
	};

	const remove = () => {
		if (!rule) return;
		Alert.alert('Delete late-fee rule?', 'This rule will no longer be applied to new overdue charges.', [
			{ text: 'Cancel', style: 'cancel' },
			{ text: 'Delete', style: 'destructive', onPress: () => del.mutate(rule.id) },
		]);
	};

	return (
		<View style={styles.section}>
			<Text style={styles.sectionTitle}>Late-fee rule</Text>
			<Text style={styles.help}>
				Charges automatically apply when an overdue charge has not been paid by the
				grace period. Example: 15 days after due date, charge $25.
			</Text>

			<View style={{ flexDirection: 'row', gap: 8 }}>
				<View style={{ flex: 1 }}>
					<Text style={styles.label}>Grace days after due date</Text>
					<TextInput style={styles.input} value={grace} onChangeText={(v) => { setGrace(v); setTouched(true); }} keyboardType="number-pad" editable={canModify} placeholder="15" placeholderTextColor="#bdbdbd" />
				</View>
				<View style={{ flex: 1 }}>
					<Text style={styles.label}>Max per cycle</Text>
					<TextInput style={styles.input} value={maxPerCycle} onChangeText={(v) => { setMaxPerCycle(v); setTouched(true); }} keyboardType="number-pad" editable={canModify} placeholder="1" placeholderTextColor="#bdbdbd" />
				</View>
			</View>

			<Text style={styles.label}>Fee type</Text>
			<View style={styles.chipRow}>
				{(['amount', 'percent'] as FeeMode[]).map((m) => (
					<Pressable key={m} disabled={!canModify} onPress={() => { setMode(m); setTouched(true); }} style={[styles.chip, mode === m && styles.chipActive, !canModify && { opacity: 0.4 }]}>
						<Text style={[styles.chipLabel, mode === m && styles.chipLabelActive]}>
							{m === 'amount' ? 'Fixed amount' : '% of overdue balance'}
						</Text>
					</Pressable>
				))}
			</View>

			{mode === 'amount' ? (
				<>
					<Text style={styles.label}>Amount</Text>
					<TextInput style={styles.input} value={amount} onChangeText={(v) => { setAmount(v); setTouched(true); }} keyboardType="decimal-pad" editable={canModify} placeholder="25.00" placeholderTextColor="#bdbdbd" />
				</>
			) : (
				<>
					<Text style={styles.label}>Percent</Text>
					<TextInput style={styles.input} value={percent} onChangeText={(v) => { setPercent(v); setTouched(true); }} keyboardType="decimal-pad" editable={canModify} placeholder="5.00" placeholderTextColor="#bdbdbd" />
				</>
			)}

			<Text style={styles.label}>Cap (optional, total fees per cycle)</Text>
			<TextInput style={styles.input} value={cap} onChangeText={(v) => { setCap(v); setTouched(true); }} keyboardType="decimal-pad" editable={canModify} placeholder="100.00" placeholderTextColor="#bdbdbd" />

			<Pressable disabled={!canModify} onPress={() => { setActive(!active); setTouched(true); }} style={[styles.toggleRow, !canModify && { opacity: 0.4 }]}>
				<View style={[styles.toggleBox, active && styles.toggleBoxOn]}>
					{active && <Text style={styles.toggleCheck}>✓</Text>}
				</View>
				<Text style={styles.toggleLabel}>Rule is active</Text>
			</Pressable>

			{canModify && !hideSaveButton && (
				<View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
					<Pressable style={[styles.primary, { flex: 1 }, pending && { opacity: 0.5 }]} disabled={pending} onPress={save}>
						<Text style={styles.primaryLabel}>{pending ? 'Saving…' : (rule ? 'Save rule' : 'Create rule')}</Text>
					</Pressable>
					{rule && (
						<Pressable style={[styles.danger, pending && { opacity: 0.5 }]} disabled={pending} onPress={remove}>
							<Text style={styles.dangerLabel}>Delete</Text>
						</Pressable>
					)}
				</View>
			)}
		</View>
	);
});

RuleEditor.displayName = 'RuleEditor';

const styles = StyleSheet.create({
	section: { marginTop: 12, padding: 14, backgroundColor: '#f5f7fa', borderRadius: 8 },
	sectionTitle: { fontSize: 14, fontWeight: '700', color: '#212121', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 },
	help: { fontSize: 12, color: '#546e7a', marginBottom: 10 },
	label: { marginTop: 8, marginBottom: 4, fontSize: 11, color: '#546e7a', fontWeight: '600', textTransform: 'uppercase' },
	input: { borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 6, padding: 10, fontSize: 14, backgroundColor: '#fff' },
	chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 4 },
	chip: { paddingVertical: 6, paddingHorizontal: 12, borderRadius: 14, backgroundColor: '#e0e0e0' },
	chipActive: { backgroundColor: '#1976d2' },
	chipLabel: { fontSize: 12, color: '#333' },
	chipLabelActive: { color: '#fff', fontWeight: '600' },
	toggleRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 12 },
	toggleBox: { width: 22, height: 22, borderRadius: 4, borderWidth: 1.5, borderColor: '#90a4ae', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff' },
	toggleBoxOn: { backgroundColor: '#1976d2', borderColor: '#1976d2' },
	toggleCheck: { color: '#fff', fontSize: 14, fontWeight: '700' },
	toggleLabel: { fontSize: 14, color: '#212121' },
	primary: { backgroundColor: '#007AFF', padding: 12, borderRadius: 6, alignItems: 'center' },
	primaryLabel: { color: '#fff', fontWeight: '600' },
	danger: { backgroundColor: '#fff', borderWidth: 1, borderColor: '#c62828', padding: 12, borderRadius: 6, alignItems: 'center', paddingHorizontal: 18 },
	dangerLabel: { color: '#c62828', fontWeight: '600' },
	historyRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6 },
	historyLabel: { fontSize: 13, color: '#546e7a', flex: 1 },
	muted: { color: '#78909c', fontSize: 11 },
});
